"use client";

import { useMemo, useState } from "react";
import { Loader2, Mail, Phone, RefreshCw } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/store";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils/utils";
import { refreshBulkBatches } from "@/hooks/useBulkBatchTracking";
import {
  bulkBatchPercent,
  selectBulkBatches,
  type BulkBatchCategory,
  type TrackedBulkBatch,
} from "@/store/slices/bulkBatchesSlice";

/**
 * "Batch Progress" in the notification panel: every bulk unlock this user has
 * running, whatever started it and whichever screen they're on now.
 *
 * This exists because a bulk run outlives the screen that started it. Select
 * 500 contacts in People Search, navigate to Workspace, and the action bar's
 * inline pills are gone - but the batch is still working through a waterfall
 * per contact for the next several minutes. Same for a Workspace column run
 * over a large sheet. The bell is the one surface mounted the whole time.
 *
 * Two sources feed it, and neither alone is enough (see bulkBatchesSlice):
 * `bulk.progress` SSE events move the bars live, and a slow poll of the two
 * list endpoints discovers batches this session never started.
 *
 * Deliberately read-only. A bulk batch can't be cancelled - Workflow-Service
 * has no such route - so offering a stop button here would be a lie.
 */

const CATEGORY_META: Record<
  BulkBatchCategory,
  { label: string; icon: typeof Mail; empty: string }
> = {
  email: {
    label: "Email unlock",
    icon: Mail,
    empty: "No email unlock running right now.",
  },
  phone: {
    label: "Phone unlock",
    icon: Phone,
    empty: "No phone unlock running right now.",
  },
};

/** Where a batch was started from - `source` is only known for batches that came back from the list endpoint. */
const SOURCE_LABEL: Record<string, string> = {
  radar: "People Search",
  workspace: "Workspace",
  enrichment: "Enrichment",
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function BatchRow({ batch }: { batch: TrackedBulkBatch }) {
  const percent = bulkBatchPercent(batch);
  const { completed, found, notFound, creditExhausted } = batch.counts;
  // Never negative: `completed` is the server's own counter and can briefly
  // exceed a `total` we learned from a stale event.
  const remaining = Math.max(0, batch.total - completed);

  return (
    <div className="rounded-lg border border-border/60 bg-card px-3 py-2.5">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="truncate font-medium text-foreground">
          {batch.source ? SOURCE_LABEL[batch.source] ?? "Bulk unlock" : "Bulk unlock"}
        </span>
        <span className="shrink-0 text-muted-foreground/80">{relativeTime(batch.createdAt)}</span>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <Progress
          value={percent}
          className={cn("h-1.5 flex-1", batch.done && "*:bg-emerald-500")}
        />
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {batch.done ? batch.total : completed}/{batch.total}
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        {batch.done ? (
          <span className="font-medium text-emerald-600 dark:text-emerald-500">Completed</span>
        ) : (
          <span>{remaining} in process</span>
        )}
        <span aria-hidden>·</span>
        <span>Found {found}</span>
        <span aria-hidden>·</span>
        <span>Not found {notFound}</span>
        {/* Only shown when it happened - the waterfall stopping early for
            credit is a different outcome from genuinely finding nothing, and
            Workflow-Service keeps the two counters apart precisely so a
            caller can say which. */}
        {creditExhausted > 0 ? (
          <>
            <span aria-hidden>·</span>
            <span className="font-medium text-amber-600 dark:text-amber-500">
              {creditExhausted} out of credit
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

function CategorySection({
  category,
  batches,
}: {
  category: BulkBatchCategory;
  batches: TrackedBulkBatch[];
}) {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  const running = batches.filter((b) => !b.done).length;

  // Summed across every batch of this category, so the collapsed header still
  // means something when a selection was split into several batches - People
  // Search chunks at 100 contacts per call, so a 500-contact run is five.
  const summary = useMemo(() => {
    let total = 0;
    let completed = 0;
    for (const b of batches) {
      total += b.total;
      completed += b.done ? b.total : b.counts.completed;
    }
    return { total, completed, percent: total > 0 ? (completed / total) * 100 : 0 };
  }, [batches]);

  return (
    <AccordionItem value={category} className="border-b-0">
      {/* items-center and py-2 override the primitive's items-start/py-4 —
          this row is a single compact line, not a multi-line heading. */}
      <AccordionTrigger className="cursor-pointer items-center rounded-lg px-2 py-2 hover:bg-muted/40 hover:no-underline [&>svg]:translate-y-0">
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">{meta.label}</span>
          {running > 0 ? (
            <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand">
              {running} running
            </span>
          ) : null}
          <span className="ml-auto mr-1 shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {summary.total > 0 ? `${summary.completed}/${summary.total}` : null}
          </span>
        </span>
      </AccordionTrigger>

      <AccordionContent className="px-2 pb-2">
        {batches.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">{meta.empty}</p>
        ) : (
          <div className="space-y-2">
            {batches.map((batch) => (
              <BatchRow key={batch.key} batch={batch} />
            ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

export function BatchProgressCard() {
  const dispatch = useAppDispatch();
  const batches = useAppSelector(selectBulkBatches);
  // Manual refresh only. The polling and the initial fetch live in the sidebar
  // (useBulkBatchTracking), which is mounted whether or not this panel is open
  // - the bell needs to know a batch is running without the user opening
  // anything, and a second poller here would just double the requests.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refresh = async () => {
    setIsRefreshing(true);
    await refreshBulkBatches(dispatch);
    setIsRefreshing(false);
  };

  const emailBatches = useMemo(() => batches.filter((b) => b.category === "email"), [batches]);
  const phoneBatches = useMemo(() => batches.filter((b) => b.category === "phone"), [batches]);
  const runningCount = batches.filter((b) => !b.done).length;

  // Open whichever side is actually doing something, so the common case needs
  // no clicks. Uncontrolled after mount on purpose - re-deriving this as
  // batches finish would snap sections shut under the user's cursor.
  const defaultOpen = useMemo(() => {
    const open: BulkBatchCategory[] = [];
    if (emailBatches.some((b) => !b.done)) open.push("email");
    if (phoneBatches.some((b) => !b.done)) open.push("phone");
    return open;
  }, []);

  return (
    <div className="shrink-0 border-t bg-muted/20 px-3 py-2.5">
      <div className="mb-1 flex items-center justify-between gap-2 px-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Batch progress
        </span>
        <div className="flex items-center gap-2">
          {runningCount > 0 ? (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              {runningCount} running
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={isRefreshing}
            aria-label="Refresh batch progress"
            className="cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isRefreshing && "animate-spin")} />
          </button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={defaultOpen}>
        <CategorySection category="email" batches={emailBatches} />
        <CategorySection category="phone" batches={phoneBatches} />
      </Accordion>
    </div>
  );
}

export default BatchProgressCard;
